// apps/frontend/src/app/dashboard/kpi/categories/replenishment.ts
import { ActivityEventProcessType } from '@warebee/shared/import-converter';

export const replenishmentKpis /*: WarehouseKPI[] */ = [
  {
    id: 'replenishment_rate',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Replenishment Rate',
    description: 'Number of replenishments completed per hour',
    unit: 'replenishments/hr',
    formula: 'COUNT(replenishments) / SUM(hours_worked)',
    tags: ['replenishment', 'productivity'],
    isPrimary: true,
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { lower: 25 },
          critical: { lower: 20 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 25 },
            { label: 'Target', min: 25.01, max: 30 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'shift', 'replenisher_id'],
    alertTopic: 'warehouse.alerts.process.replenishment_rate',
    relationships: [
      {
        relatedKpiId: 'uph_picking',
        relationshipType: 'causes',
        strength: 0.8,
        lag: 'PT30M',
        description:
          'Replenishment directly feeds picking operations. A higher replenishment rate ensures pick locations are well-stocked, preventing stockouts that would slow down pickers.',
      },
      {
        relatedKpiId: 'picking_accuracy',
        relationshipType: 'causes',
        strength: 0.5,
        lag: 'PT1H',
        description:
          'Proper and timely replenishment reduces the likelihood of pickers grabbing incorrect substitute items when the correct item is out of stock.',
      },
      {
        relatedKpiId: 'avg_distance_per_picklist',
        relationshipType: 'causes',
        strength: 0.6,
        lag: 'PT30M',
        description:
          'Timely replenishment of forward pick locations reduces the need for pickers to travel to reserve locations.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['bar', 'gauge'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'shift', 'replenisher_id'],
      annotateWith: {
        eventTypes: ['layout_change', 'system_outage', 'training'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'replenishmentType',
        label: 'Replenishment Type',
        description: 'Filter by replenishment type',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'reserve_to_pick',
          'bulk_to_case',
          'case_to_each',
          'external',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'priorityLevel',
        label: 'Priority Level',
        description: 'Filter by priority level',
        type: 'enum',
        defaultValue: 'all',
        enumValues: ['all', 'critical', 'high', 'normal', 'low'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
    recommendedActions: [
      {
        kpiId: 'replenishment_rate',
        recommendedAction: 'Investigate causes of low replenishment rate',
        tools: [
          {
            id: 'root-cause',
            name: 'Root Cause Analysis',
            description:
              'Analyze underlying issues affecting replenishment performance',
            category: 'rootCauseAnalysis',
            link: '/tools/root-cause',
          },
        ],
        steps: [
          'Check for stockouts at pick faces',
          'Review replenisher staffing and shift coverage',
          'Analyze recent slotting or layout changes',
        ],
        priority: 'high',
      },
      {
        kpiId: 'replenishment_rate',
        recommendedAction:
          'Optimize replenishment task assignment and slotting',
        tools: [
          {
            id: 'slotting-tool',
            name: 'Slotting Optimization',
            description:
              'Optimize replenishment slotting to reduce travel distance and time',
            category: 'Optimize',
            link: '/tools/slotting',
          },
          {
            id: 'labor-planner',
            name: 'Labor Planning',
            description: 'Adjust labor allocation for peak periods',
            category: 'ScheduleLabour',
            link: '/tools/labor-planner',
          },
        ],
        steps: [
          'Run slotting simulation for replenishment tasks',
          'Adjust replenisher assignments for high-velocity zones',
        ],
        priority: 'medium',
      },
    ],
  },
  {
    id: 'replenishment_picking_correlation',
    processType: [
      ActivityEventProcessType.REPLENISHMENT,
      ActivityEventProcessType.PICKING,
    ],
    category: 'insights',
    title: 'Replenishment-Picking Correlation',
    description:
      'Measures how closely replenishment rates correlate with picking productivity',
    unit: '%',
    formula: 'CORRELATION(replenishment_rate, picking_rate) * 100',
    tags: ['replenishment', 'picking', 'correlation', 'insights'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'Digital twin simulation',
        },
      },
      primarySource: 'digitalTwin',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 100 },
          ],
        },
      ],
      metadata: {
        source: 'digital twin model',
        lastUpdated: '2023-04-15',
      },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'item_category', 'time_of_day'],
    alertTopic: 'warehouse.alerts.insights.replenishment_picking_correlation',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'influenced_by',
        strength: 1.0,
        description:
          'This correlation metric directly measures how replenishment rates affect picking.',
      },
      {
        relatedKpiId: 'uph_picking',
        relationshipType: 'influenced_by',
        strength: 1.0,
        description:
          'This correlation metric directly measures how picking rates are affected by replenishment.',
      },
      {
        relatedKpiId: 'stockout_frequency',
        relationshipType: 'influenced_by',
        strength: 0.8,
        description:
          'Stockout frequency strongly affects the correlation between replenishment and picking.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'heatmap',
      alternativeTypes: ['line', 'scatter'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: false,
      showThresholds: true,
      recommendedDimensions: ['zone', 'time_of_day'],
      annotateWith: {
        eventTypes: ['layout_change', 'process_change'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'lagTime',
        label: 'Lag Time',
        description:
          'Time lag between replenishment and expected effect on picking',
        type: 'enum',
        defaultValue: 'PT30M',
        enumValues: ['PT15M', 'PT30M', 'PT1H', 'PT2H', 'PT4H'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'correlationMethod',
        label: 'Correlation Method',
        description: 'Statistical method used for correlation',
        type: 'enum',
        defaultValue: 'pearson',
        enumValues: ['pearson', 'spearman', 'kendall'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'avg_replenishment_distance',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Average Replenishment Distance',
    description: 'Average travel distance per replenishment task',
    unit: 'm',
    formula: 'AVG(replenishment_distance)',
    tags: ['replenishment', 'distance', 'efficiency'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 75,
          warning: { upper: 100 },
          critical: { upper: 150 },
          basis: 'Travel optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 75 },
            { label: 'Warning', min: 75.01, max: 100 },
            { label: 'Critical', min: 100.01, max: 200 },
          ],
        },
      ],
      metadata: { source: 'travel analysis' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'replenisher_id', 'replenishment_type'],
    alertTopic: 'warehouse.alerts.process.avg_replenishment_distance',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'opposing',
        strength: 0.7,
        description:
          'Longer travel distances reduce the number of replenishments that can be completed per hour.',
      },
      {
        relatedKpiId: 'avg_distance_per_picklist',
        relationshipType: 'causes',
        strength: 0.6,
        description:
          'Efficient replenishment travel patterns typically result in better pick location layouts and reduced picker travel.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['bar', 'heatmap'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'replenisher_id', 'replenishment_type'],
      annotateWith: {
        eventTypes: ['layout_change'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'replenishmentType',
        label: 'Replenishment Type',
        description: 'Filter by replenishment type',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'reserve_to_pick',
          'bulk_to_case',
          'case_to_each',
          'external',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'avg_replenishment_time',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Average Replenishment Time',
    description: 'Average time to complete a replenishment task',
    unit: 'min',
    formula: 'AVG(replenishment_time_minutes)',
    tags: ['replenishment', 'time', 'efficiency'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 8 },
          critical: { upper: 12 },
          basis: 'Time efficiency standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 8 },
            { label: 'Critical', min: 8.01, max: 20 },
          ],
        },
      ],
      metadata: { source: 'time study' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'replenisher_id', 'replenishment_type'],
    alertTopic: 'warehouse.alerts.process.avg_replenishment_time',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'opposing',
        strength: 0.9,
        description:
          'Longer task times directly reduce the number of replenishments that can be completed per hour.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['bar', 'gauge'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'replenisher_id', 'replenishment_type'],
      annotateWith: {
        eventTypes: ['training', 'process_change'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'replenishmentType',
        label: 'Replenishment Type',
        description: 'Filter by replenishment type',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'reserve_to_pick',
          'bulk_to_case',
          'case_to_each',
          'external',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'min_distance_to_pick_location',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Min Distance to Pick Location',
    description: 'Minimum distance between reserve storage and pick locations',
    unit: 'm',
    formula: 'MIN(reserve_to_pick_distance)',
    tags: ['replenishment', 'distance', 'slotting'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 20 },
          critical: { upper: 30 },
          basis: 'Slotting optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 20 },
            { label: 'Critical', min: 20.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'slotting analysis' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'item_category', 'replenishment_type'],
    alertTopic: 'warehouse.alerts.process.min_distance_to_pick_location',
    relationships: [
      {
        relatedKpiId: 'avg_replenishment_time',
        relationshipType: 'causes',
        strength: 0.5,
        description:
          'Shorter distances between reserve and pick locations reduce replenishment time.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'bar',
      alternativeTypes: ['heatmap'],
      defaultTimeRange: 'P28D',
      aggregation: 'min',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'item_category'],
      annotateWith: {
        eventTypes: ['layout_change', 'slotting_change'],
        timeMarkers: false,
      },
    },
    variables: [
      {
        name: 'itemVelocity',
        label: 'Item Velocity',
        description: 'Filter by item velocity category',
        type: 'enum',
        defaultValue: 'all',
        enumValues: ['all', 'a_fast', 'b_medium', 'c_slow'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'max_distance_to_pick_location',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Max Distance to Pick Location',
    description: 'Maximum distance between reserve storage and pick locations',
    unit: 'm',
    formula: 'MAX(reserve_to_pick_distance)',
    tags: ['replenishment', 'distance', 'slotting'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 100,
          warning: { upper: 150 },
          critical: { upper: 200 },
          basis: 'Slotting optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 100 },
            { label: 'Warning', min: 100.01, max: 150 },
            { label: 'Critical', min: 150.01, max: 300 },
          ],
        },
      ],
      metadata: { source: 'slotting analysis' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'item_category', 'replenishment_type'],
    alertTopic: 'warehouse.alerts.process.max_distance_to_pick_location',
    relationships: [
      {
        relatedKpiId: 'avg_replenishment_time',
        relationshipType: 'causes',
        strength: 0.7,
        description:
          'Excessive distances between reserve and pick locations significantly increase replenishment time.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'bar',
      alternativeTypes: ['heatmap'],
      defaultTimeRange: 'P28D',
      aggregation: 'max',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'item_category'],
      annotateWith: {
        eventTypes: ['layout_change', 'slotting_change'],
        timeMarkers: false,
      },
    },
    variables: [
      {
        name: 'itemVelocity',
        label: 'Item Velocity',
        description: 'Filter by item velocity category',
        type: 'enum',
        defaultValue: 'all',
        enumValues: ['all', 'a_fast', 'b_medium', 'c_slow'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'avg_distance_to_pick_location',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Avg Distance to Pick Location',
    description: 'Average distance between reserve storage and pick locations',
    unit: 'm',
    formula: 'AVG(reserve_to_pick_distance)',
    tags: ['replenishment', 'distance', 'slotting'],
    isPrimary: true,
    benchmark: {
      standards: {
        site: {
          target: 50,
          warning: { upper: 75 },
          critical: { upper: 100 },
          basis: 'Slotting optimization standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 50 },
            { label: 'Warning', min: 50.01, max: 75 },
            { label: 'Critical', min: 75.01, max: 150 },
          ],
        },
      ],
      metadata: { source: 'slotting analysis' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['zone', 'item_category', 'replenishment_type'],
    alertTopic: 'warehouse.alerts.process.avg_distance_to_pick_location',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'causes',
        strength: 0.8,
        description:
          'Average distance between reserve and pick locations directly impacts replenishment efficiency.',
      },
      {
        relatedKpiId: 'uph_picking',
        relationshipType: 'causes',
        strength: 0.6,
        lag: 'PT1H',
        description:
          'Optimized slotting with reduced distances improves replenishment speed, which in turn supports higher picking rates.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'line',
      alternativeTypes: ['bar', 'heatmap'],
      defaultTimeRange: 'P28D',
      aggregation: 'avg',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'item_category'],
      annotateWith: {
        eventTypes: ['layout_change', 'slotting_change'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'itemVelocity',
        label: 'Item Velocity',
        description: 'Filter by item velocity category',
        type: 'enum',
        defaultValue: 'all',
        enumValues: ['all', 'a_fast', 'b_medium', 'c_slow'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Time Window',
        description: 'Analysis period',
        type: 'enum',
        defaultValue: 'P28D',
        enumValues: ['P1D', 'P7D', 'P28D', 'P90D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
  {
    id: 'replenishment_jobs_by_workzone',
    processType: ActivityEventProcessType.REPLENISHMENT,
    category: 'process',
    title: 'Replenishment Jobs by Workzone',
    description: 'Number of replenishment jobs by workzone and time frame',
    unit: 'jobs',
    formula: 'COUNT(replenishment_jobs) GROUP BY zone, time_frame',
    tags: ['replenishment', 'workload', 'planning'],
    isPrimary: false,
    benchmark: {
      standards: {
        site: {
          target: 50,
          warning: { upper: 75, lower: 30 },
          critical: { upper: 100, lower: 20 },
          basis: 'Workload balancing standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical Low', min: 0, max: 20 },
            { label: 'Warning Low', min: 20.01, max: 30 },
            { label: 'Target', min: 30.01, max: 75 },
            { label: 'Warning High', min: 75.01, max: 100 },
            { label: 'Critical High', min: 100.01, max: 200 },
          ],
        },
      ],
      metadata: { source: 'workload analysis' },
    },
    trendWindow: 'P7D',
    drillDimensions: [
      'zone',
      'time_of_day',
      'replenishment_type',
      'priority_level',
    ],
    alertTopic: 'warehouse.alerts.process.replenishment_jobs_by_workzone',
    relationships: [
      {
        relatedKpiId: 'replenishment_rate',
        relationshipType: 'causes',
        strength: 0.7,
        description:
          'The distribution of replenishment jobs across workzones affects overall replenishment efficiency.',
      },
      {
        relatedKpiId: 'uph_picking',
        relationshipType: 'causes',
        strength: 0.6,
        lag: 'PT2H',
        description:
          'Balanced replenishment workload across zones ensures consistent support for picking operations.',
      },
    ],
    recommendedVisualization: {
      primaryType: 'heatmap',
      alternativeTypes: ['bar', 'line'],
      defaultTimeRange: 'P7D',
      aggregation: 'sum',
      showTarget: true,
      showThresholds: true,
      recommendedDimensions: ['zone', 'time_frame'],
      annotateWith: {
        eventTypes: ['staffing_change', 'process_change'],
        timeMarkers: true,
      },
    },
    variables: [
      {
        name: 'timeFrame',
        label: 'Time Frame',
        description: 'Time frame granularity',
        type: 'enum',
        defaultValue: 'hourly',
        enumValues: ['hourly', 'shift', 'daily', 'weekly'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'replenishmentType',
        label: 'Replenishment Type',
        description: 'Filter by replenishment type',
        type: 'enum',
        defaultValue: 'all',
        enumValues: [
          'all',
          'reserve_to_pick',
          'bulk_to_case',
          'case_to_each',
          'external',
        ],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'priorityLevel',
        label: 'Priority Level',
        description: 'Filter by priority level',
        type: 'enum',
        defaultValue: 'all',
        enumValues: ['all', 'critical', 'high', 'normal', 'low'],
        userConfigurable: true,
        uiComponent: 'dropdown',
      },
      {
        name: 'timeWindow',
        label: 'Analysis Period',
        description: 'Time window for analysis',
        type: 'enum',
        defaultValue: 'P7D',
        enumValues: ['P1D', 'P3D', 'P7D', 'P14D', 'P28D'],
        userConfigurable: true,
        uiComponent: 'dropdown',
        affectsBenchmark: true,
      },
    ],
  },
];

export default replenishmentKpis;
